import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useShortlist } from "@/context/ShortlistContext";
import { langNL } from "@/config/languageLabels";
import { profileImage } from "@/lib/images";
import { Loader2, CheckCircle2, ChevronLeft, ChevronRight, Upload, X, FileText, Pencil, Check } from "lucide-react";

const PROJECT_TYPES = ["Commercial", "Online video", "Corporate", "E-learning", "Telefonie", "VR / Gaming", "Anders"];
const USAGE_RELEVANT = ["Commercial", "Online video"];
const GENDERS = ["Jongen", "Meisje", "Geen voorkeur"];
const SCRIPT_LENGTHS = [
  "10–60 seconden / 10–65 woorden",
  "1–5 minuten / 130–650 woorden",
  "5–10 minuten / 650–1300 woorden",
  "Meer dan 10 minuten / meer dan 1300 woorden",
  "Anders",
];
const TERRITORIES = ["Nederland", "België", "Benelux", "Europa", "Wereldwijd", "Anders"];
const DURATIONS = ["1 maand", "3 maanden", "6 maanden", "1 jaar", "2 jaar", "Eeuwigdurend", "Anders"];

const empty = {
  project_name: "", project_type: "", languages: "", country_accent: "",
  gender_pref: "", voice_age: "", style: "",
  script_length: "", script_length_other: "", deadline: "",
  usage_territory: "", usage_territory_other: "", usage_duration: "", usage_duration_other: "",
  script_text: "", script_file_url: "", script_file_name: "", comments: "",
  remote_direction: "",
  first_name: "", last_name: "", company: "", email: "", phone: "",
};

export default function BookingForm({ talents = [], selectedIds, onSelectedChange, onSubmitted, initialProjectName = "" }) {
  const [form, setForm] = useState({ ...empty, project_name: initialProjectName });
  const [step, setStep] = useState(0);
  const [reviewing, setReviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const shortlist = useShortlist();
  const controlled = typeof onSelectedChange === "function";
  const sourceTalents = talents.length ? talents : (shortlist?.items || []);

  // Selection state: controlled by parent (Shortlist page) or internal (default = all favorites).
  const [internalSel, setInternalSel] = useState([]);
  useEffect(() => {
    if (controlled || talents.length) return;
    const all = (shortlist?.items || []).map((i) => i.id);
    setInternalSel((prev) => {
      const kept = prev.filter((id) => all.includes(id));
      return kept.length ? kept : all;
    });
  }, [controlled, talents.length, shortlist?.items]);

  const selIds = controlled ? (selectedIds || []) : internalSel;
  const setSel = controlled ? onSelectedChange : setInternalSel;
  const selected = sourceTalents.filter((t) => selIds.includes(t.id));

  const usageRelevant = USAGE_RELEVANT.includes(form.project_type);
  const steps = useMemo(
    () => (usageRelevant ? ["project", "gebruik", "script", "casting", "gegevens"] : ["project", "script", "casting", "gegevens"]),
    [usageRelevant]
  );
  const LABEL = { project: "Project", gebruik: "Gebruik", script: "Script", casting: "Casting", gegevens: "Gegevens" };
  const current = steps[Math.min(step, steps.length - 1)];

  const territoryValue = form.usage_territory === "Anders" ? form.usage_territory_other : form.usage_territory;
  const durationValue = form.usage_duration === "Anders" ? form.usage_duration_other : form.usage_duration;
  const scriptLengthValue = form.script_length === "Anders" ? form.script_length_other : form.script_length;

  const validateStep = (key) => {
    if (key === "project") {
      if (!form.project_type) return "Kies een type productie.";
      if (!form.languages.trim()) return "Geef de taal / talen aan.";
      if (!form.script_length) return "Geef de scriptlengte aan.";
      if (form.script_length === "Anders" && !form.script_length_other.trim()) return "Vul de scriptlengte in.";
    }
    if (key === "gebruik") {
      if (!form.usage_territory) return "Kies waar de opname wordt gebruikt.";
      if (form.usage_territory === "Anders" && !form.usage_territory_other.trim()) return "Vul het gebied in.";
      if (!form.usage_duration) return "Kies de gebruiksduur.";
      if (form.usage_duration === "Anders" && !form.usage_duration_other.trim()) return "Vul de gebruiksduur in.";
    }
    if (key === "gegevens") {
      if (!form.first_name.trim()) return "Vul je voornaam in.";
      if (!form.last_name.trim()) return "Vul je achternaam in.";
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return "Vul een geldig e-mailadres in.";
    }
    return null;
  };

  const next = () => {
    const err = validateStep(current);
    if (err) { toast.error(err); return; }
    if (step >= steps.length - 1) { setReviewing(true); window.scrollTo({ top: 0, behavior: "smooth" }); }
    else setStep(step + 1);
  };
  const back = () => { if (step > 0) setStep(step - 1); };
  const goToStep = (key) => { setReviewing(false); setStep(Math.max(0, steps.indexOf(key))); };

  const toggleVoice = (id) => {
    const nextIds = selIds.includes(id) ? selIds.filter((x) => x !== id) : [...selIds, id];
    setSel(nextIds);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/casting/upload-script", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("script_file_url", r.data.url);
      set("script_file_name", r.data.filename);
      toast.success("Script geüpload.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Uploaden mislukt. Probeer het opnieuw.");
    } finally { setUploading(false); }
  };

  const submit = async () => {
    setSending(true);
    try {
      await api.post("/casting", {
        first_name: form.first_name, last_name: form.last_name, company: form.company,
        name: `${form.first_name} ${form.last_name}`.trim(),
        email: form.email, phone: form.phone,
        project_name: form.project_name,
        project_type: form.project_type,
        languages: form.languages,
        country_accent: form.country_accent,
        gender_pref: form.gender_pref,
        voice_age: form.voice_age,
        style: form.style,
        script_length: scriptLengthValue,
        deadline: form.deadline,
        remote_direction: form.remote_direction === "Ja",
        usage_territory: usageRelevant ? territoryValue : "",
        usage_duration: usageRelevant ? durationValue : "",
        script: form.script_text,
        script_file_url: form.script_file_url,
        script_file_name: form.script_file_name,
        brief: form.comments,
        selected_talent_ids: selected.map((t) => t.id),
        selected_talent_names: selected.map((t) => t.name),
      });
      if (window.gtag) window.gtag("event", "booking_request_submitted");
      setDone(true);
      if (onSubmitted) onSubmitted();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Er ging iets mis. Mail ons op bookings@voiceoverkids.agency");
    } finally { setSending(false); }
  };

  if (done) {
    return (
      <div data-testid="booking-success" className="rounded-2xl border border-green-200 bg-green-50 p-8 text-center">
        <CheckCircle2 className="mx-auto mb-3 text-green-600" size={44} />
        <h3 className="font-heading text-2xl font-bold text-zinc-900">Je boekingsaanvraag is ontvangen.</h3>
        <p className="mx-auto mt-3 max-w-lg text-zinc-600">
          We controleren de beschikbaarheid van de geselecteerde stem(men) en nemen contact met je op over de prijs en definitieve bevestiging.
        </p>
      </div>
    );
  }

  const usageDisplay = (v) => (usageRelevant ? (v || "—") : "N.v.t.");

  return (
    <div data-testid="booking-form">
      {/* Progress indicator */}
      {!reviewing && (
        <ol className="mb-8 flex items-center gap-2" data-testid="booking-progress">
          {steps.map((s, i) => (
            <li key={s} className="flex flex-1 items-center gap-2">
              <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold transition ${
                i < step ? "bg-brand-violet text-white" : i === step ? "bg-brand-violet text-white ring-4 ring-violet-100" : "bg-zinc-100 text-zinc-400"}`}>
                {i < step ? <Check size={15} /> : i + 1}
              </div>
              <span className={`hidden text-sm font-semibold sm:block ${i === step ? "text-zinc-900" : "text-zinc-400"}`}>{LABEL[s]}</span>
              {i < steps.length - 1 && <span className="h-px flex-1 bg-zinc-200" />}
            </li>
          ))}
        </ol>
      )}

      {/* ---------------- Review ---------------- */}
      {reviewing ? (
        <div data-testid="booking-review" className="space-y-4">
          <h3 className="font-heading text-2xl font-bold">Je boekingsaanvraag</h3>
          <ReviewRow label="Kind / stemtalent naam" onEdit={() => goToStep("project")}>{form.project_name || "—"}</ReviewRow>
          <ReviewRow label="Type productie" onEdit={() => goToStep("project")}>{form.project_type || "—"}</ReviewRow>
          <ReviewRow label="Taal / talen" onEdit={() => goToStep("project")}>{form.languages || "—"}</ReviewRow>
          <ReviewRow label="Land / accent" onEdit={() => goToStep("project")}>{form.country_accent || "—"}</ReviewRow>
          <ReviewRow label="Geslacht" onEdit={() => goToStep("project")}>{form.gender_pref || "—"}</ReviewRow>
          <ReviewRow label="Stemleeftijd" onEdit={() => goToStep("project")}>{form.voice_age || "—"}</ReviewRow>
          <ReviewRow label="Stemstijl / toon" onEdit={() => goToStep("project")}>{form.style || "—"}</ReviewRow>
          <ReviewRow label="Scriptlengte" onEdit={() => goToStep("project")}>{scriptLengthValue || "—"}</ReviewRow>
          <ReviewRow label="Gewenste opleverdatum" onEdit={() => goToStep("project")}>{form.deadline || "—"}</ReviewRow>
          <ReviewRow label="Gebruiksgebied" onEdit={usageRelevant ? () => goToStep("gebruik") : null}>{usageDisplay(territoryValue)}</ReviewRow>
          <ReviewRow label="Gebruiksduur" onEdit={usageRelevant ? () => goToStep("gebruik") : null}>{usageDisplay(durationValue)}</ReviewRow>
          <ReviewRow label="Regie op afstand" onEdit={() => goToStep("casting")}>{form.remote_direction || "—"}</ReviewRow>
          <ReviewRow label="Geselecteerde stemmen" onEdit={() => goToStep("casting")}>{selected.length ? selected.map((t) => t.name).join(", ") : "—"}</ReviewRow>
          <ReviewRow label="Script" onEdit={() => goToStep("script")}>{form.script_file_name || (form.script_text ? "Geplakt script" : "—")}</ReviewRow>
          <ReviewRow label="Instructies / opmerkingen" onEdit={() => goToStep("script")}>{form.comments || "—"}</ReviewRow>
          <ReviewRow label="Contact" onEdit={() => goToStep("gegevens")}>
            {`${form.first_name} ${form.last_name}`.trim()}{form.company ? ` · ${form.company}` : ""} · {form.email}{form.phone ? ` · ${form.phone}` : ""}
          </ReviewRow>

          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            Dit is nog geen definitieve boeking. We controleren de beschikbaarheid van de stem(men) en sturen je daarna de prijs en bevestiging.
          </div>
          <div className="flex flex-wrap gap-3">
            <button type="button" onClick={() => { setReviewing(false); setStep(steps.length - 1); }}
              className="inline-flex items-center gap-2 rounded-full border border-zinc-300 px-6 py-3 font-semibold text-zinc-700 hover:bg-zinc-50">
              <ChevronLeft size={16} /> Terug
            </button>
            <button type="button" onClick={submit} disabled={sending} data-testid="booking-submit"
              className="inline-flex items-center gap-2 rounded-full bg-brand-violet px-8 py-3 font-bold text-white shadow-lg shadow-violet-500/25 transition hover:bg-brand-violetHover disabled:opacity-60">
              {sending && <Loader2 size={18} className="animate-spin" />} Boekingsaanvraag versturen
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* ---------------- Step: Project ---------------- */}
          {current === "project" && (
            <div className="space-y-6" data-testid="step-project">
              <Field label="Heb je al een specifiek kind / stemtalent in gedachten? (optioneel)">
                <Input value={form.project_name} onChange={(e) => set("project_name", e.target.value)} placeholder="Naam van de stem" className="h-11 rounded-xl" data-testid="bf-project-name" />
              </Field>
              <Field label="Type productie">
                <div className="grid gap-2 sm:grid-cols-2">
                  {PROJECT_TYPES.map((o) => <Radio key={o} name="pt" checked={form.project_type === o} onChange={() => set("project_type", o)} label={o} testid={`pt-${o}`} />)}
                </div>
              </Field>
              <Field label="Taal / talen *">
                <Input value={form.languages} onChange={(e) => set("languages", e.target.value)} placeholder="Bijv. Nederlands, Engels" className="h-11 rounded-xl" data-testid="bf-languages" />
              </Field>
              <Field label="Land / accent (optioneel)">
                <Input value={form.country_accent} onChange={(e) => set("country_accent", e.target.value)} placeholder="Bijv. Vlaams, Amerikaans" className="h-11 rounded-xl" data-testid="bf-country" />
              </Field>
              <Field label="Geslacht (optioneel)">
                <div className="grid gap-2 sm:grid-cols-3">
                  {GENDERS.map((o) => <Radio key={o} name="gender" checked={form.gender_pref === o} onChange={() => set("gender_pref", o)} label={o} testid={`gender-${o}`} />)}
                </div>
              </Field>
              <Field label="Stemleeftijd (optioneel)">
                <Input value={form.voice_age} onChange={(e) => set("voice_age", e.target.value)} placeholder="Beschrijf de gewenste stemleeftijd of -range (niet per se de echte leeftijd van het kind)" className="h-11 rounded-xl" data-testid="bf-voice-age" />
              </Field>
              <Field label="Stemstijl / toon (optioneel)">
                <Input value={form.style} onChange={(e) => set("style", e.target.value)} placeholder="Bijv. vrolijk, warm, energiek" className="h-11 rounded-xl" data-testid="bf-style" />
              </Field>
              <Field label="Hoe lang is het script?">
                <div className="space-y-2">
                  {SCRIPT_LENGTHS.map((o) => <Radio key={o} name="sl" checked={form.script_length === o} onChange={() => set("script_length", o)} label={o} />)}
                </div>
                {form.script_length === "Anders" && (
                  <Input value={form.script_length_other} onChange={(e) => set("script_length_other", e.target.value)}
                    placeholder="Geef de scriptlengte aan" className="mt-2 h-11 rounded-xl" data-testid="sl-other" />
                )}
              </Field>
              <Field label="Gewenste opleverdatum (optioneel)">
                <Input type="date" value={form.deadline} onChange={(e) => set("deadline", e.target.value)} className="h-11 rounded-xl sm:max-w-xs" data-testid="bf-deadline" />
              </Field>
            </div>
          )}

          {/* ---------------- Step: Gebruik ---------------- */}
          {current === "gebruik" && (
            <div className="space-y-6" data-testid="step-gebruik">
              <p className="rounded-xl bg-zinc-50 p-3 text-sm text-zinc-500">Deze informatie gebruiken we om eventuele gebruiksrechten/buyouts te berekenen.</p>
              <Field label="Waar wordt de opname gebruikt?">
                <div className="grid gap-2 sm:grid-cols-2">
                  {TERRITORIES.map((o) => <Radio key={o} name="ter" checked={form.usage_territory === o} onChange={() => set("usage_territory", o)} label={o} />)}
                </div>
                {form.usage_territory === "Anders" && (
                  <Input value={form.usage_territory_other} onChange={(e) => set("usage_territory_other", e.target.value)} placeholder="Geef het gebied aan" className="mt-2 h-11 rounded-xl" />
                )}
              </Field>
              <Field label="Hoe lang wil je de opname gebruiken?">
                <div className="grid gap-2 sm:grid-cols-2">
                  {DURATIONS.map((o) => <Radio key={o} name="dur" checked={form.usage_duration === o} onChange={() => set("usage_duration", o)} label={o} />)}
                </div>
                {form.usage_duration === "Anders" && (
                  <Input value={form.usage_duration_other} onChange={(e) => set("usage_duration_other", e.target.value)} placeholder="Geef de gebruiksduur aan" className="mt-2 h-11 rounded-xl" />
                )}
              </Field>
            </div>
          )}

          {/* ---------------- Step: Script ---------------- */}
          {current === "script" && (
            <div className="space-y-6" data-testid="step-script">
              <Field label="Script (optioneel)">
                <p className="mb-2 text-sm text-zinc-500">Upload een bestand of plak de tekst hieronder. Nog geen script? Dat mag je later nasturen.</p>
                <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.txt,.rtf,.odt,.pages,.md" onChange={onFile} className="hidden" data-testid="script-file-input" />
                {form.script_file_name ? (
                  <div className="flex items-center gap-3 rounded-xl border border-zinc-200 bg-white p-3">
                    <FileText size={18} className="text-brand-violet" />
                    <span className="flex-1 truncate text-sm font-medium text-zinc-800">{form.script_file_name}</span>
                    <button type="button" onClick={() => { set("script_file_url", ""); set("script_file_name", ""); }} className="text-zinc-400 hover:text-red-500"><X size={16} /></button>
                  </div>
                ) : (
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="script-upload-btn"
                    className="inline-flex items-center gap-2 rounded-xl border border-dashed border-zinc-300 px-5 py-3 text-sm font-semibold text-zinc-600 hover:border-brand-violet hover:text-brand-violet disabled:opacity-60">
                    {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Bestand uploaden
                  </button>
                )}
                <Textarea value={form.script_text} onChange={(e) => set("script_text", e.target.value)} rows={5}
                  placeholder="…of plak hier je script" className="mt-3 rounded-xl" data-testid="script-textarea" />
              </Field>
              <Field label="Instructies / opmerkingen (optioneel)">
                <Textarea value={form.comments} onChange={(e) => set("comments", e.target.value)} rows={3}
                  placeholder="Denk aan tone of voice, uitspraak, referenties of andere instructies." className="rounded-xl" data-testid="comments-textarea" />
              </Field>
            </div>
          )}

          {/* ---------------- Step: Casting ---------------- */}
          {current === "casting" && (
            <div className="space-y-6" data-testid="step-casting">
              <Field label="Is regie op afstand nodig?">
                <div className="grid gap-2 sm:grid-cols-2">
                  {["Ja", "Nee"].map((o) => <Radio key={o} name="remote" checked={form.remote_direction === o} onChange={() => set("remote_direction", o)} label={o} testid={`remote-${o}`} />)}
                </div>
              </Field>
              <Field label="Welke van je favoriete stemmen zullen we meenemen?">
                {sourceTalents.length === 0 ? (
                  <p className="rounded-xl bg-zinc-50 p-3 text-sm text-zinc-500">Je hebt nog geen stemmen als favoriet opgeslagen. Bekijk de stemmen en tik op het hartje om ze hier te kunnen selecteren.</p>
                ) : (
                  <div className="grid gap-2 sm:grid-cols-2">
                    {sourceTalents.map((t) => {
                      const on = selIds.includes(t.id);
                      return (
                        <label key={t.id} data-testid={`sel-toggle-${t.id}`}
                          className={`flex cursor-pointer items-center gap-3 rounded-xl border p-2.5 transition ${on ? "border-brand-violet bg-violet-50" : "border-zinc-200 bg-white hover:border-zinc-300"}`}>
                          <Checkbox checked={on} onCheckedChange={() => toggleVoice(t.id)} />
                          <div className="h-9 w-9 overflow-hidden rounded-lg bg-zinc-100">
                            <img src={profileImage(t)} alt={t.name} className="h-full w-full object-cover" />
                          </div>
                          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-zinc-800">{t.name}</span>
                          {langNL(t.language) && <span className="shrink-0 text-xs text-zinc-400">{langNL(t.language)}</span>}
                        </label>
                      );
                    })}
                  </div>
                )}
              </Field>
            </div>
          )}

          {/* ---------------- Step: Gegevens ---------------- */}
          {current === "gegevens" && (
            <div className="grid gap-5 sm:grid-cols-2" data-testid="step-gegevens">
              <Field label="Voornaam *"><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} placeholder="Voornaam" className="h-11 rounded-xl" data-testid="bf-first" /></Field>
              <Field label="Achternaam *"><Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} placeholder="Achternaam" className="h-11 rounded-xl" data-testid="bf-last" /></Field>
              <Field label="Bedrijf (optioneel)"><Input value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Bedrijfsnaam" className="h-11 rounded-xl" data-testid="bf-company" /></Field>
              <Field label="E-mailadres *"><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="naam@voorbeeld.nl" className="h-11 rounded-xl" data-testid="bf-email" /></Field>
              <Field label="Telefoonnummer (optioneel)"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+31 6 …" className="h-11 rounded-xl" data-testid="bf-phone" /></Field>
            </div>
          )}

          {/* Navigation */}
          <div className="mt-8 flex items-center justify-between">
            <button type="button" onClick={back} disabled={step === 0}
              className="inline-flex items-center gap-2 rounded-full border border-zinc-300 px-6 py-3 font-semibold text-zinc-700 transition hover:bg-zinc-50 disabled:opacity-40" data-testid="booking-back">
              <ChevronLeft size={16} /> Terug
            </button>
            <button type="button" onClick={next} data-testid="booking-next"
              className="inline-flex items-center gap-2 rounded-full bg-brand-violet px-8 py-3 font-bold text-white shadow-lg shadow-violet-500/25 transition hover:bg-brand-violetHover">
              {step >= steps.length - 1 ? "Controleren" : "Volgende"} <ChevronRight size={16} />
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="mb-2 block font-heading text-base font-bold text-zinc-900">{label}</Label>
      {children}
    </div>
  );
}

function Radio({ name, checked, onChange, label, testid }) {
  return (
    <label data-testid={testid} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 text-sm transition ${
      checked ? "border-brand-violet bg-violet-50 font-semibold text-zinc-900" : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300"}`}>
      <span className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${checked ? "border-brand-violet" : "border-zinc-300"}`}>
        {checked && <span className="h-2.5 w-2.5 rounded-full bg-brand-violet" />}
      </span>
      <input type="radio" name={name} checked={checked} onChange={onChange} className="sr-only" />
      {label}
    </label>
  );
}

function ReviewRow({ label, children, onEdit }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-zinc-200 bg-white p-4">
      <div className="min-w-0">
        <span className="label-eyebrow text-zinc-400">{label}</span>
        <p className="mt-1 whitespace-pre-line break-words text-zinc-800">{children}</p>
      </div>
      {onEdit && <button type="button" onClick={onEdit} className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-brand-violet hover:underline"><Pencil size={13} /> Wijzig</button>}
    </div>
  );
}
